import React, { createContext, useContext, useState, useCallback } from 'react';
import en from './translations/en.json';
import it from './translations/it.json';

const translations = { en, it };

// Add new languages here: create a JSON in ./translations/, import it, add it to the object above
export const supportedLanguages = [
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'it', label: 'Italiano', flag: '🇮🇹' },
];

const DEFAULT_LANGUAGE = 'en';
const STORAGE_KEY = 'app_language';

export const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && translations[saved] ? saved : DEFAULT_LANGUAGE;
  });

  const setLanguage = useCallback((lang) => {
    if (translations[lang]) {
      setLanguageState(lang);
      localStorage.setItem(STORAGE_KEY, lang);
    } else {
      console.warn(`Language "${lang}" is not supported. Available: ${Object.keys(translations).join(', ')}`);
    }
  }, []);

  // Resolves dot-notation keys (e.g. 'privacyPolicy.title') against the active language,
  // falling back to DEFAULT_LANGUAGE if the key is missing, then to the raw key string.
  const t = useCallback((key) => {
    const keys = key.split('.');

    const resolve = (obj) => keys.reduce((acc, k) => (acc && typeof acc === 'object' && k in acc ? acc[k] : undefined), obj);

    const result = resolve(translations[language]);
    if (result !== undefined) return result;

    const fallback = resolve(translations[DEFAULT_LANGUAGE]);
    if (fallback !== undefined) return fallback;

    console.warn(`Translation key "${key}" not found in "${language}" or "${DEFAULT_LANGUAGE}".`);
    return key;
  }, [language]);

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Must be used inside a LanguageProvider. Returns { t, language, setLanguage }.
export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};